import { useMutation } from '@tanstack/react-query'
import { api } from '@api'
import { appendAvatarToFormData } from '../../../utils/fileUpload'

interface OnboardingResponse {}

interface OnboardingParams {
  onboardingParams: OnboardingData
  token: string
}
interface OnboardingData {
  avatar?: string
  residence?: string
  birthDate: string
  firstName: string
  hashtags: string[]
  moments: string[]
  languages: Array<{ code: string; level?: string }>
  lastName: string
  nationalities: string[]
  origins?: string[]
  bio?: string
  goals?: string[]
  hobbies?: string[]
  skills?: string[]
  gender: 'male' | 'female' | 'non-binary'
  contacts?: {
    email?: string | null
    phone?: string | null
    website?: string | null
    facebook?: string | null
    instagram?: string | null
    twitter?: string | null
    linkedin?: string | null
  }
  latitude?: number | null
  longitude?: number | null
}

const completeOnboarding = async ({ onboardingParams, token }: OnboardingParams) => {
  const formData = new FormData()

  // Append avatar using shared utility
  if (onboardingParams.avatar) {
    appendAvatarToFormData(formData, onboardingParams.avatar, 'avatar')
  }

  if (onboardingParams.residence) {
    formData.append('residence', onboardingParams.residence)
  }
  formData.append('birthDate', onboardingParams.birthDate)
  formData.append('firstName', onboardingParams.firstName)
  formData.append('lastName', onboardingParams.lastName)
  if (onboardingParams.origins) {
    if (onboardingParams.origins.length > 0) {
      onboardingParams.origins.forEach((origin, index) => {
        formData.append(`origins[${index}]`, origin)
      })
    } else {
      formData.append('origins', JSON.stringify([]))
    }
  }
  // Backend requires `bio` to be a present string (`@IsString()`), so always
  // append it. The Step1 schema marks it optional, so default to '' when the
  // user skipped the "À propos de toi" field.
  formData.append('bio', onboardingParams.bio ?? '')
  formData.append('gender', onboardingParams.gender)

  // Add goals, hobbies, skills if provided
  if (onboardingParams.goals && onboardingParams.goals.length > 0) {
    onboardingParams.goals.forEach((goal, index) => {
      formData.append(`goals[${index}]`, goal)
    })
  }
  if (onboardingParams.hobbies && onboardingParams.hobbies.length > 0) {
    onboardingParams.hobbies.forEach((hobby, index) => {
      formData.append(`hobbies[${index}]`, hobby)
    })
  }
  if (onboardingParams.skills && onboardingParams.skills.length > 0) {
    onboardingParams.skills.forEach((skill, index) => {
      formData.append(`skills[${index}]`, skill)
    })
  }

  onboardingParams.nationalities.forEach((nationality, index) => {
    formData.append(`nationalities[${index}]`, nationality)
  })

  onboardingParams.hashtags.forEach((hashtag, index) => {
    formData.append(`hashtags[${index}]`, hashtag)
  })

  const availabilities = {
    slots: [],
    momentIds: onboardingParams.moments
  }

  formData.append('availabilities', JSON.stringify(availabilities))

  onboardingParams.languages.forEach((language, index) => {
    formData.append(`languages[${index}][code]`, language.code)
    if (language.level) {
      formData.append(`languages[${index}][level]`, language.level)
    }
  })

  // Add contacts if provided
  if (onboardingParams.contacts) {
    formData.append('contacts', JSON.stringify(onboardingParams.contacts))
  }
  if (onboardingParams.latitude != null) {
    formData.append('latitude', String(onboardingParams.latitude))
  }
  if (onboardingParams.longitude != null) {
    formData.append('longitude', String(onboardingParams.longitude))
  }

  const { data } = await api.post<OnboardingResponse>('/onboarding', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: token,
    },
  })

  return data
}

export const useCompleteOnboarding = () => {
  return useMutation<OnboardingResponse, Error, OnboardingParams>({
    mutationFn: completeOnboarding,
  })
}
